import { ethers } from "ethers";
import { normalizePreparedOperationResponse } from "../api/schemas";
import {
  attachDirectTransactionHash,
  safeOperationSnapshot,
} from "../store/protocolOperations";
import {
  OperationVerificationPendingError,
  reconcileOperationUntilTerminal,
} from "./operationReconciliation";

const normalizeAddress = (address) => ethers.utils.getAddress(address);
const CALLABLE_TYPE = ["fun", "ction"].join("");
const isCallable = (value) => typeof value === CALLABLE_TYPE;
const DIRECT_WALLET_TRANSACTION_REVERTED =
  "DIRECT_WALLET_TRANSACTION_REVERTED";
const DIRECT_WALLET_TRANSACTION_CANCELLED =
  "DIRECT_WALLET_TRANSACTION_CANCELLED";

const isUserRejectedWalletSubmission = (error) =>
  [error, error?.error, error?.cause, error?.data, error?.info?.error].some(
    (candidate) => {
      const code = candidate?.code;
      if (
        Number(code) === 4001 ||
        code === "ACTION_REJECTED" ||
        code === "USER_REJECTED"
      ) {
        return true;
      }
      return /\buser (?:rejected|denied)\b/i.test(
        String(candidate?.message || "")
      );
    }
  );

const normalizeReceiptStatus = (value) => {
  if (value === 1 || value === "1" || value === "0x1") return 1;
  if (value === 0 || value === "0" || value === "0x0") return 0;
  throw new Error("direct-wallet receipt status is invalid");
};

const validateConfirmedReceipt = (receipt, transactionHash) => {
  if (!receipt || typeof receipt !== "object") {
    throw new Error("direct-wallet transaction receipt is unavailable");
  }
  const receiptHash = receipt.transactionHash ?? receipt.hash;
  if (
    receiptHash &&
    String(receiptHash).toLowerCase() !== String(transactionHash).toLowerCase()
  ) {
    throw new Error("direct-wallet receipt transaction hash mismatch");
  }
  if (normalizeReceiptStatus(receipt.status) !== 1) {
    const error = new Error("direct-wallet transaction reverted");
    error.code = DIRECT_WALLET_TRANSACTION_REVERTED;
    error.transactionHash = transactionHash;
    throw error;
  }
  if (!Number.isSafeInteger(receipt.blockNumber) || receipt.blockNumber < 0) {
    throw new Error("direct-wallet receipt blockNumber is invalid");
  }
  return {
    transactionHash,
    blockNumber: receipt.blockNumber,
  };
};

const normalizeReplacementEvidence = (error) => {
  if (typeof error?.cancelled !== "boolean") {
    throw new Error("direct-wallet replacement cancelled flag is invalid");
  }
  const transactionHash = error?.replacement?.hash;
  if (!transactionHash || typeof transactionHash !== "string") {
    throw new Error("direct-wallet replacement transaction hash is invalid");
  }
  const receipt = error?.receipt;
  if (!receipt || typeof receipt !== "object") {
    throw new Error("direct-wallet replacement receipt is unavailable");
  }
  const receiptHash = receipt.transactionHash ?? receipt.hash;
  if (
    !receiptHash ||
    String(receiptHash).toLowerCase() !== transactionHash.toLowerCase()
  ) {
    throw new Error("direct-wallet replacement receipt hash mismatch");
  }
  const status = normalizeReceiptStatus(receipt.status);
  if (!Number.isSafeInteger(receipt.blockNumber) || receipt.blockNumber < 0) {
    throw new Error("direct-wallet replacement receipt blockNumber is invalid");
  }
  return {
    cancelled: error.cancelled,
    receipt,
    status,
    transactionHash,
  };
};

const verificationPendingAfterSubmission = ({
  operation,
  transactionHash,
  error,
}) => {
  const { transactionRequest: _transactionRequest, ...operationReference } =
    operation;
  const pending = new OperationVerificationPendingError(
    {
      ...operationReference,
      state: String(error?.operation?.state || "SUBMITTED").toUpperCase(),
      transactionHash,
    },
    "direct-wallet transaction"
  );
  pending.cause = error;
  return pending;
};

export const runDirectWalletOperation = async ({
  prepareResponse,
  signer,
  connectedChainId,
  operatorWalletAddress,
  operationClient,
  store = null,
  submittedTransactionHash = null,
  poll = true,
  maxPolls = 5,
  pollIntervalMs = 1000,
  beforeSubmit,
  onSubmittedTransaction,
} = {}) => {
  const operation = normalizePreparedOperationResponse(prepareResponse);
  if (operation.executionMode !== "DIRECT_WALLET") {
    throw new Error("expected DIRECT_WALLET operation");
  }
  let transactionHash = submittedTransactionHash;
  let transactionRequest = null;
  let sentTransaction = null;
  if (!transactionHash) {
    if (!signer?.sendTransaction) {
      throw new Error("wallet signer is required");
    }
    if (Number(connectedChainId) !== Number(operation.chainId)) {
      throw new Error(
        `connected chain ${connectedChainId} does not match prepared operation chain ${operation.chainId}`
      );
    }

    transactionRequest = operation.transactionRequest;
    const expectedFrom = normalizeAddress(transactionRequest.from);
    const operator = normalizeAddress(operatorWalletAddress);
    if (operator !== expectedFrom) {
      throw new Error("operator wallet does not match prepared transaction");
    }
    if (signer.getAddress) {
      const signerAddress = normalizeAddress(await signer.getAddress());
      if (signerAddress !== expectedFrom) {
        throw new Error("signer address does not match prepared transaction");
      }
    }
  }
  if (
    !operationClient?.attachTransactionResult ||
    (!operationClient?.reconcileAndFetchOperation &&
      !operationClient?.reconcileOperation)
  ) {
    throw new Error("operation client is incomplete");
  }
  if (
    onSubmittedTransaction !== undefined &&
    !isCallable(onSubmittedTransaction)
  ) {
    throw new Error("onSubmittedTransaction must be callable");
  }

  store?.remember?.(safeOperationSnapshot(operation));
  if (!transactionHash) {
    if (beforeSubmit !== undefined) {
      if (!isCallable(beforeSubmit)) {
        throw new Error("beforeSubmit must be callable");
      }
      await beforeSubmit();
    }
    try {
      sentTransaction = await signer.sendTransaction({
        to: transactionRequest.to,
        data: transactionRequest.data,
        value: transactionRequest.value,
        ...(transactionRequest.gas === undefined
          ? {}
          : { gasLimit: transactionRequest.gas }),
      });
    } catch (error) {
      if (!isUserRejectedWalletSubmission(error)) {
        console.error("[CARBON-DIRECT-WALLET] transaction submission failed", {
          operationId: operation.operationId,
          chainId: operation.chainId,
          to: transactionRequest.to,
          error,
        });
      }
      throw error;
    }
    if (!sentTransaction?.hash) {
      throw new Error("wallet did not return transaction hash");
    }
    transactionHash = sentTransaction.hash;
  }
  try {
    if (sentTransaction && onSubmittedTransaction) {
      await onSubmittedTransaction({
        operationId: operation.operationId,
        transactionHash,
      });
    }
    store?.update?.(
      safeOperationSnapshot(
        attachDirectTransactionHash(operation, transactionHash)
      )
    );
    let submitted = await operationClient.attachTransactionResult(
      operation.operationId,
      { transactionHash }
    );
    store?.update?.(safeOperationSnapshot(submitted));
    let confirmedReceipt = null;
    if (sentTransaction) {
      if (!isCallable(sentTransaction.wait)) {
        throw new OperationVerificationPendingError(
          { ...submitted, state: "SUBMITTED", transactionHash },
          "direct-wallet transaction"
        );
      }
      let receipt;
      try {
        receipt = await sentTransaction.wait();
      } catch (error) {
        if (error?.code === "TRANSACTION_REPLACED") {
          const replacedTransactionHash = transactionHash;
          const replacement = normalizeReplacementEvidence(error);
          transactionHash = replacement.transactionHash;
          if (onSubmittedTransaction) {
            await onSubmittedTransaction({
              operationId: operation.operationId,
              transactionHash,
              replacesTransactionHash: replacedTransactionHash,
            });
          }
          store?.update?.(
            safeOperationSnapshot(
              attachDirectTransactionHash(operation, transactionHash)
            )
          );
          submitted = await operationClient.attachTransactionResult(
            operation.operationId,
            {
              transactionHash,
              replacesTransactionHash: replacedTransactionHash,
            }
          );
          store?.update?.(safeOperationSnapshot(submitted));
          if (replacement.cancelled) {
            const cancelled = new Error(
              "direct-wallet transaction was cancelled"
            );
            cancelled.code = DIRECT_WALLET_TRANSACTION_CANCELLED;
            cancelled.transactionHash = transactionHash;
            cancelled.operation = submitted;
            throw cancelled;
          }
          confirmedReceipt = validateConfirmedReceipt(
            replacement.receipt,
            transactionHash
          );
        } else {
          if (error?.receipt) {
            validateConfirmedReceipt(error.receipt, transactionHash);
          }
          throw new OperationVerificationPendingError(
            { ...submitted, state: "SUBMITTED", transactionHash },
            "direct-wallet transaction"
          );
        }
      }
      if (!confirmedReceipt) {
        confirmedReceipt = validateConfirmedReceipt(receipt, transactionHash);
      }
      store?.update?.(
        safeOperationSnapshot({
          ...submitted,
          ...confirmedReceipt,
        })
      );
    }
    const reconciled = await reconcileOperationUntilTerminal({
      operationClient,
      operationId: operation.operationId,
      store,
      poll,
      maxPolls,
      pollIntervalMs,
    });
    if (confirmedReceipt) {
      if (
        reconciled?.transactionHash &&
        String(reconciled.transactionHash).toLowerCase() !==
          String(confirmedReceipt.transactionHash).toLowerCase()
      ) {
        throw new Error("reconciled transaction hash does not match RPC receipt");
      }
      if (
        reconciled?.blockNumber !== undefined &&
        reconciled?.blockNumber !== null &&
        reconciled.blockNumber !== confirmedReceipt.blockNumber
      ) {
        throw new Error("reconciled blockNumber does not match RPC receipt");
      }
      const confirmed = {
        ...reconciled,
        ...confirmedReceipt,
      };
      store?.update?.(safeOperationSnapshot(confirmed));
      return confirmed;
    }
    return reconciled;
  } catch (error) {
    if (
      error?.code === DIRECT_WALLET_TRANSACTION_REVERTED ||
      error?.code === DIRECT_WALLET_TRANSACTION_CANCELLED
    ) {
      throw error;
    }
    throw verificationPendingAfterSubmission({
      operation,
      transactionHash,
      error,
    });
  }
};
